import React, { useState, useEffect, useRef, useCallback } from "react";
import "./style/signDocument.scss";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store/store";
import { clearAuth } from "../../store/slices/authSlice";
import SignatureCanvas from "react-signature-canvas";
import { ClipLoader } from "react-spinners";
import { showError } from "../../services/toastservice/toastService";
import { touchlessService } from "../../services/api/touchlessService";
import { logAuthDiagnostic } from "../../services/diagnostics/authDiagnostic";
import type { LocationItem } from "../../types/orgConfig";
import type {
  DocumentTemplate,
  VisitPayload,
} from "../../services/api/touchlessService";
import type { ApiError } from "../../types/api";
import ConfirmationModal from "../../components/common/ConfirmationModal/ConfirmationModal";
import { deleteAllCookies } from "../../utils/helpers";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { BackButton } from "../../components/ui/Button/backButton";
import { useStrings } from "../../hooks/useStrings";
import { shouldShowIdCapture } from "../../utils/idCapture";
import { shouldShowVideo } from "../../utils/videoStep";
import {
  renderDocTemplate,
  escapeHtml,
  customFieldKey,
} from "../../utils/sanitizeDocTemplate";
const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  return d
    .toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    .replace(",", "");
};

interface DocumentSignInfo {
  orgDocTemplateId: string;
  signedDocUri?: string;
  docSignatureImage?: string;
  // EN-4724: true when the visitor declined this document instead of signing it.
  declined?: boolean;
}

interface VisitorInfo extends VisitPayload {
  id?: string;
  preRegisterVisitId?: string;
  visitorTypeId?: string;
  videoWatched?: boolean;
  cohosts?: { cohostName: string; [key: string]: string }[];
}

const SignDocument: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const signatureRef = useRef<SignatureCanvas>(null);
  const signatureBoxRef = useRef<HTMLDivElement>(null);
  const mainScreenRef = useRef<HTMLDivElement>(null);

  // State variables matching Angular component
  const { config: orgConfig, themeColors } = useAppConfig();
  const {
    visitorinfo: visitorInfo,
    videoUrl,
    orgTemplateIds,
    flowInfoFlags,
    idValidationSkip,
    videoSkip,
    updateVisitorInfo,
  } = useVisitorData();

  const [pdfVisitorDocument, setPdfVisitorDocument] = useState<string>("");
  const [visitorDate] = useState(new Date());
  const [documentName, setDocumentName] = useState<string>("");
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [docTemplateId, setDocTemplateId] = useState<string>("");
  const [skipSigningFlag, setSkipSigningFlag] = useState<boolean>(false);
  const [enableAgree, setEnableAgree] = useState<boolean>(true);
  const [documentSignArray, setDocumentSignArray] = useState<
    DocumentSignInfo[]
  >([]);
  const [docSignatureImage, setDocSignatureImage] = useState<string>("");
  const [documentCurrent, setDocumentCurrent] = useState<number>(0);
  const [documentObject, setDocumentObject] = useState<DocumentTemplate[]>([]);

  const [todayDate] = useState(new Date());
  const [loading, setLoadingState] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [expandedTop, setExpandedTop] = useState<number>(0);

  const signaturePadOptions = {
    minWidth: 1,
    canvasProps: {
      width: signatureBoxRef.current?.clientWidth || 150,
      height: signatureBoxRef.current?.clientHeight || 100,
      className: "signature-canvas",
    },
  };

  const getCohostsNames = (visitorInfoData: VisitorInfo): string => {
    return touchlessService.isNullUndefinedOrBlank(visitorInfoData?.cohosts)
      ? ""
      : visitorInfoData?.cohosts
          ?.map(
            (item: { cohostName: string; [key: string]: string }) =>
              item.cohostName,
          )
          .join(", ") || "";
  };

  const dataDisplay = (
    docObjects: DocumentTemplate[] = documentObject,
    currentIndex: number,
    visitorInfoData: VisitorInfo | null,
  ) => {
    const visitorTodayDate = visitorDate.toISOString().split("T")[0];

    setSkipSigningFlag(docObjects[currentIndex]?.skipSigningFlag || false);
    setDocumentName(docObjects[currentIndex]?.name);
    setDocumentUrl(docObjects[currentIndex]?.docTemplateUri);
    setDocTemplateId(docObjects[currentIndex]?.id);

    const updatedVisitorInfo: VisitorInfo = {
      ...(visitorInfoData as VisitorInfo),
      skipSigningFlag: docObjects[currentIndex]?.skipSigningFlag,
    };
    updateVisitorInfo(updatedVisitorInfo);

    fetch(docObjects[currentIndex]?.docTemplateUri)
      .then((res) => res.text())
      .then((rawHtml) => {
        const val = (v: string | undefined | null) =>
          touchlessService.isNullUndefinedOrBlank(v) ? "" : (v as string);

        const placeholders: Record<string, string> = {
          "{{company}}": updatedVisitorInfo.companyName || "",
          "{{visitor_name}}": updatedVisitorInfo.fullName || "",
          "{{org_name}}": orgConfig?.orgName || "",
          "{{checkin_date}}": visitorTodayDate,
          "{{pointOfEntry}}": val(updatedVisitorInfo.poeName),
          "{{parkingLot}}": val(updatedVisitorInfo.parkingLotName),
          "{{building}}": val(updatedVisitorInfo.buildingName),
          "{{visitor_phone}}": val(updatedVisitorInfo.phoneNumber),
          "{{visitor_email}}": val(updatedVisitorInfo.email),
          "{{host_name}}": val(updatedVisitorInfo.hostName),
          "{{visitor_type}}": val(updatedVisitorInfo.visitorType),
          "{{location_name}}": val(updatedVisitorInfo.siteName),
          "{{cohosts_names}}": getCohostsNames(updatedVisitorInfo),
        };

        const customFieldModels =
          updatedVisitorInfo.visitCustomFieldModels &&
          updatedVisitorInfo.visitCustomFieldModels.length > 0
            ? updatedVisitorInfo.visitCustomFieldModels
            : updatedVisitorInfo.preregisterVisitCustomFieldModels;

        const cfMap = new Map<string, string>();
        customFieldModels?.forEach(
          (element: {
            orgCustomFieldId: string;
            value: string;
            name: string;
          }) => {
            const key = customFieldKey(element.name || "");
            if (key) cfMap.set(key, escapeHtml(element.value || ""));
          },
        );
        const CF_RE = /\{\{([\w]+)\}\}/g;
        const htmlWithCustomFields = rawHtml.replace(
          CF_RE,
          (token, name: string) => {
            const trimmed = name.replace(/^_+|_+$/g, "");
            return cfMap.has(trimmed) ? cfMap.get(trimmed)! : token;
          },
        );

        setPdfVisitorDocument(renderDocTemplate(htmlWithCustomFields, placeholders));
      })
      .catch((error) => {
        console.error("Error fetching document content:", error);
        showError(S.SIGN_DOCUMENT.ERROR_LOADING_CONTENT);
      });
  };

  const fetchDocumentMultiple = (visitorInfoData: VisitorInfo) => {
    const documentObjectArray: DocumentTemplate[] = [];
    if (!orgTemplateIds) {
      console.error(
        `SignDocument: No template IDs provided, site ${orgConfig?.siteName}, visitorType ${visitorInfo?.visitorType} `,
      );
      return;
    }
    for (let i = 0; i < orgTemplateIds.length; i++) {
      touchlessService
        .getNDADocument(orgTemplateIds[i])
        .then((data: DocumentTemplate) => {
          if (data) {
            documentObjectArray.push(data);
            // Display the first document when we have recieve all documents
            if (documentObjectArray.length === orgTemplateIds.length) {
              setDocumentObject(documentObjectArray);
              dataDisplay(documentObjectArray, 0, visitorInfoData);
            }
          }
        })
        .catch((error: ApiError) => {
          setLoadingState(false);
          console.error(
            `SignDocument: Error fetching document: ${orgTemplateIds[i]}, site ${orgConfig?.siteName}, visitorType ${visitorInfo?.visitorType}`,
            error,
          );
          // Structured diagnostic to Middleware: captures sessionStorage,
          // Redux state, page, and the failing doctemplate so we can
          // correlate with backend logs.
          logAuthDiagnostic({
            reason: "sign_document_fetch_failed",
            apiUrl: `/v1/doctemplates/${orgTemplateIds[i]}`,
            apiMethod: "GET",
            errorStatus: (error as { response?: { status?: number } })?.response?.status,
            errorMessage: (error as { message?: string })?.message,
            errorCode: (error as { code?: string })?.code,
            extra: {
              docTemplateId: orgTemplateIds[i],
              visitorType: visitorInfo?.visitorType,
              docIndexInBatch: i,
              docBatchSize: orgTemplateIds.length,
            },
          });
          showError(S.SIGN_DOCUMENT.ERROR_FETCHING_DOCUMENT);
        });
    }
  };

  const setParkingFieldsInVisitorInfo = useCallback(() => {
    const updatedVisitorInfo = {
      ...visitorInfo,
      poeName: orgConfig?.pointsOfEntry?.find(
        (element: LocationItem) => element && element.id === visitorInfo?.poeId,
      )?.name,
      parkingLotName: orgConfig?.parkingLots?.find(
        (element: LocationItem) => element.id === visitorInfo?.parkingLotId,
      )?.name,
      buildingName: orgConfig?.buildings?.find(
        (element: LocationItem) => element.id === visitorInfo?.buildingId,
      )?.name,
    };
    updateVisitorInfo(updatedVisitorInfo);
    return updatedVisitorInfo as VisitorInfo;
  }, [visitorInfo, orgConfig, updateVisitorInfo]);

  useEffect(() => {
    if (
      !visitorInfo ||
      !orgConfig ||
      touchlessService.isNullUndefinedOrBlank(orgTemplateIds) ||
      (orgTemplateIds && orgTemplateIds.length === 0)
    ) {
      showError(S.SIGN_DOCUMENT.ERROR_RESTORE_SESSION);
      navigate("/");
      return;
    }

    const updatedVisitorInfo = setParkingFieldsInVisitorInfo();
    fetchDocumentMultiple(updatedVisitorInfo);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDocumentSubmit = () => {
    if (documentObject.length === documentCurrent + 1) {
      const newDocumentSignArray = [
        ...documentSignArray,
        {
          signedDocUri: documentUrl,
          orgDocTemplateId: docTemplateId,
          docSignatureImage: docSignatureImage,
        },
      ];

      const updatedVisitorInfo = {
        ...visitorInfo,
        skipSigningFlag: false,
        visitSignedDocsInfos: newDocumentSignArray,
      };

      updateVisitorInfo(updatedVisitorInfo);

      if (
        shouldShowIdCapture(flowInfoFlags?.isIdCapturingFlag, idValidationSkip)
      ) {
        navigate("/capturepicture");
      } else if (flowInfoFlags?.visitorPhotoFlag) {
        navigate("/visitorpicture");
      } else {
        if (shouldShowVideo(videoUrl, videoSkip)) {
          navigate("/visitor-video");
        } else {
          navigate("/confirmation", { replace: true });
        }
      }
    } else {
      const newDocumentSignArray = [
        ...documentSignArray,
        {
          signedDocUri: documentUrl,
          orgDocTemplateId: docTemplateId,
          docSignatureImage: docSignatureImage,
        },
      ];

      setDocumentSignArray(newDocumentSignArray);
      setDocumentName("");
      setDocTemplateId("");
      clearSignaturePad();
      setDocSignatureImage("");

      const nextIndex = documentCurrent + 1;
      setDocumentCurrent(nextIndex);
      dataDisplay(documentObject, nextIndex, visitorInfo as VisitorInfo);
    }
  };

  const onDocumentDeclineSubmit = () => {
    // EN-4724: record which document was declined, same array the accept path
    // already populates — without this, the decline reached the backend with
    // no reference to the document at all and was silently dropped.
    const newDocumentSignArray = [
      ...documentSignArray,
      { orgDocTemplateId: docTemplateId, declined: true },
    ];

    const updatedVisitorInfo = {
      ...visitorInfo,
      skipSigningFlag: true,
      visitSignedDocsInfos: newDocumentSignArray,
    };
    updateVisitorInfo(updatedVisitorInfo);
    if (
      shouldShowIdCapture(flowInfoFlags?.isIdCapturingFlag, idValidationSkip)
    ) {
      navigate("/capturepicture");
    } else if (flowInfoFlags?.visitorPhotoFlag) {
      navigate("/visitorpicture");
    } else {
      if (shouldShowVideo(videoUrl, videoSkip)) {
        navigate("/visitor-video");
      } else {
        navigate("/confirmation", { replace: true });
      }
    }
  };

  const drawComplete = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatureDataURL = signatureRef.current.toDataURL();
      const base64Data = signatureDataURL.replace("data:image/png;base64,", "");
      setDocSignatureImage(base64Data);
    }
    updateVisitorInfo(visitorInfo);
    setEnableAgree(false);
  };

  const clearSignaturePad = () => {
    setDocSignatureImage("");
    setEnableAgree(true);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const onBack = () => {
    navigate("/visitorform");
  };

  const handleCancelConfirm = () => {
    dispatch(clearAuth());
    sessionStorage.clear();
    deleteAllCookies();
    setShowCancelModal(false);
    navigate("/");
  };

  return (
    <>
      <div className="main-screen main-screen--desktop" data-testid="SD-div-main-screen" ref={mainScreenRef}>
        <div
          className="screen-outer screen-signdocument"
          id="sign-document"
          data-testid="SD-div-screen-outer"
        >
          <div
            className="screen-header-fixed"
            data-testid="SD-div-screen-header-fixed"
          >
            <div
              className="header-fixed-left"
              data-testid="SD-div-header-fixed-left"
            >
              <BackButton onBack={onBack} dataTestId="Sign-doc-back" />
            </div>
            <div
              className="header-fixed-right"
              data-testid="SD-div-header-fixed-right"
            ></div>
          </div>

          <div className="screen-inner" data-testid="SD-div-screen-inner">
            <div
              className="screen-subinner"
              data-testid="SD-div-screen-subinner"
            >
              <div className="screen-body" data-testid="SD-div-screen-body">
                <h5 className="screen-title" data-testid="SD-h5-screen-title">
                  {S.SIGN_DOCUMENT.TITLE_PREFIX
                    ? S.SIGN_DOCUMENT.TITLE_PREFIX + " "
                    : ""}
                  {documentName}
                </h5>
                <p
                  className="screen-subtitle"
                  data-testid="SD-p-screen-subtitle"
                >
                  {S.SIGN_DOCUMENT.SUBTITLE}
                </p>
                <div
                  className={`pdf-visitor${isExpanded ? " pdf-visitor--expanded" : ""}`}
                  style={isExpanded ? { top: expandedTop, height: `calc(100vh - ${expandedTop}px - 44px)` } : undefined}
                  data-testid="SD-div-pdf-visitor"
                >
                  <div className="pdf-visitor-header">
                    <button
                      className="expand-btn"
                      onClick={() => {
                        if (!isExpanded && mainScreenRef.current) {
                          setExpandedTop(mainScreenRef.current.getBoundingClientRect().top);
                        }
                        setIsExpanded(!isExpanded);
                      }}
                      title={isExpanded ? S.SIGN_DOCUMENT.BTN_COLLAPSE : S.SIGN_DOCUMENT.BTN_EXPAND}
                      data-testid="SD-button-expand"
                    >
                      <i className={`bi ${isExpanded ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`}></i>
                    </button>
                  </div>
                  <div
                    className="pdf-visitor-content"
                    dangerouslySetInnerHTML={{ __html: pdfVisitorDocument }}
                  />
                </div>
              </div>
              <div
                className="screen-footer"
                data-testid="SD-div-screen-footer-fixed"
              >
                <div
                  className="pdf-signature"
                  data-testid="SD-div-pdf-signature"
                >
                  <div
                    className="pdf-sign"
                    data-testid="SD-div-pdf-sign"
                    ref={signatureBoxRef}
                  >
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={signaturePadOptions.canvasProps}
                      onEnd={drawComplete}
                      data-testid="SD-canvas-signature"
                    />
                  </div>
                  <div
                    className="sign-details"
                    data-testid="SD-div-sign-details"
                  >
                    <p className="sign-info" data-testid="SD-p-sign-info">
                      {visitorInfo?.fullName ? visitorInfo.fullName + "," : ""}{" "}
                      {formatDateDisplay(todayDate.toISOString().split("T")[0])}
                    </p>
                    <Button
                      className="btn btn-link"
                      onClick={clearSignaturePad}
                      data-testid="SD-button-clear"
                    >
                      <i
                        className="bi bi-eraser-fill"
                        data-testid="SD-icon-clear"
                      ></i>{" "}
                      {S.COMMON.CLEAR_SIGNATURE}
                    </Button>
                  </div>
                </div>
                {skipSigningFlag && (
                  <div
                    className="mt-4 decline-continue-wrapper"
                    data-testid="SD-div-decline-continue-wrapper"
                  >
                    <button
                      type="button"
                      className="btn btn-secondary btn-shadow"
                      onClick={onDocumentDeclineSubmit}
                      data-testid="SD-button-decline"
                    >
                      {S.COMMON.DECLINE}
                    </button>
                    <button
                      type="button"
                      disabled={enableAgree}
                      className={`btn btn-primary btn-shadow`}
                      onClick={onDocumentSubmit}
                      data-testid="SD-button-agree"
                    >
                      {S.COMMON.AGREE_AND_CONTINUE} &nbsp;&nbsp;
                      <i className="bi bi-arrow-right-short"></i>
                    </button>
                  </div>
                )}
                {!skipSigningFlag && (
                  <button
                    type="button"
                    disabled={enableAgree}
                    className={`btn btn-primary btn-shadow btn-width-full`}
                    onClick={onDocumentSubmit}
                    data-testid="SD-button-agree"
                  >
                    {S.COMMON.AGREE_AND_CONTINUE} &nbsp;&nbsp;
                    <i className="bi bi-arrow-right-short"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-overlay" data-testid="SD-div-loading-overlay">
            <ClipLoader
              color={themeColors.btnColor}
              loading={loading}
              size={50}
              data-testid="SD-loader-clip"
            />
          </div>
        )}
      </div>
      <ConfirmationModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        data-testid="SD-modal-confirmation"
      />
    </>
  );
};

export default SignDocument;
